import axios from 'axios';
import emptyFunction from 'fbjs/lib/emptyFunction';
import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';
import { withAlert } from 'react-alert';
import { IntlProvider } from 'react-intl';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withContext,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import history from '../../core/history';
import { en } from '../../languages/en';
import { fr } from '../../languages/fr';
import GenericErrorPage from '../../routes/error/GenericErrorPage';
import Header from '../Header/Header';
import LoadingProgress from '../LoadingProgress/LoadingProgress';
import Sidebar from '../Sidebar';
import s from './App.module.css';
import withMe from './withMe';

axios.defaults.withCredentials = true;

const langMessage = {
  en: en.messages,
  fr: fr.messages,
};

const AppWithMePure = ({
  data,
  lang,
  newChildren,
  message,
  partnerNumber,
  permissions,
  role,
}) => (
  <IntlProvider
    defaultLocale={fr.locale}
    defaultRichTextElements={{
      br: () => <br />,
    }}
    locale={lang}
    messages={message}
  >
    <div className={`dashboard-page ${s.dashboardPage}`}>
      <Header data={data} partnerNumber={partnerNumber} />
      <Sidebar
        partnerNumber={partnerNumber}
        role={role}
        permissions={permissions}
      />
      <section id={s.bodyContainer} className={s.uiView}>
        {newChildren}
      </section>
    </div>
  </IntlProvider>
);

const AppWithMe = compose(
  withContext(
    {
      successMsg: PropTypes.func,
      errorMsg: PropTypes.func,
      selectedLink: PropTypes.object,
      partnerNumber: PropTypes.string,
      handleCurrentPageChanged: PropTypes.func,
      currentPage: PropTypes.object,
    },
    (p) => ({
      successMsg: p.successMsg,
      errorMsg: p.errorMsg,
      selectedLink: p.selectedLink,
      partnerNumber: p.partnerNumber,
      handleCurrentPageChanged: p.handleCurrentPageChanged,
      currentPage: p.currentPage,
    }),
  ),
)(AppWithMePure);

const LoadingApp = () => <LoadingProgress show />;

const ResetPassword = ({ children, lang, message }) => (
  <IntlProvider locale={lang} messages={message}>
    {children}
  </IntlProvider>
);

const Login = lazy(() => import('../../routes/login/Login'));

const LoginComponent = ({ lang, message, msgHooks, onLoginSuccess }) => (
  <IntlProvider locale={lang} messages={message}>
    <Suspense fallback={<LoadingProgress show />}>
      <Login key="login" onLoginSuccess={onLoginSuccess} {...msgHooks} />
    </Suspense>
  </IntlProvider>
);

const withLangState = withState('lang', 'setLang', fr.locale);
const withMessageState = withState('message', 'setMessage', fr.messages);

const getChildContext = withContext(
  {
    setLang: PropTypes.func,
    setMeta: PropTypes.func,
    setTitle: PropTypes.func,
  },
  ({ context, setLang, setMessage }) => ({
    setLang: (lang) => setLang(lang, () => setMessage(langMessage[lang])),
    setMeta: context.setMeta || emptyFunction,
    setTitle: context.setTitle || emptyFunction,
  }),
);

const msgHooksHandlers = withHandlers({
  errorMsg:
    ({ alert }) =>
    (...args) =>
      alert.error(...args),
  infoMsg:
    ({ alert }) =>
    (...args) =>
      alert.info(...args),
  showMsg:
    ({ alert }) =>
    (...args) =>
      alert.show(...args),
  successMsg:
    ({ alert }) =>
    (...args) =>
      alert.success(...args),
  removeMsg:
    ({ alert }) =>
    () =>
      alert.removeAll(),
});

const loginHandlers = withHandlers({
  onLoginSuccess:
    ({ data }) =>
    () =>
      data.refetch(),
});

const pageHandlers = withHandlers({
  handleCurrentPageChanged:
    ({ setCurrentPage }) =>
    (page) =>
      setCurrentPage(page, () => (document.title = page.pageTitle)),
});

const appProps = withProps(
  ({ children, data, me, msgHooks, partnerNumber, withoutExtraProps }) => {
    if (!partnerNumber && me?.links?.length > 0) {
      history.replace(`/${me.links[0].partnerAccount.partnerNumber}`);
    }

    const newChildren = withoutExtraProps
      ? children
      : {
          ...children,
          props: {
            connectedUser: data.metaQuery.me,
            partnerChannel: partnerNumber,
            ...msgHooks,
            ...children.props,
          },
        };

    let permissions, role, selectedLink;
    if (partnerNumber) {
      const linksWithCorrectPartnerNumber = me.links.filter(
        (l) => l.partnerAccount.partnerNumber === partnerNumber,
      );

      if (
        linksWithCorrectPartnerNumber &&
        linksWithCorrectPartnerNumber.length > 0
      ) {
        selectedLink = linksWithCorrectPartnerNumber[0];

        if (selectedLink) {
          role = selectedLink.defaultRoleName;
          permissions = selectedLink.defaultPermissions;
        }
      }
    }

    return {
      newChildren,
      permissions,
      role,
      selectedLink,
    };
  },
);

const withLifecycle = lifecycle({
  componentDidMount() {
    const { setLang, setMessage } = this.props;

    window.scrollTo(0, 0);
    const lang = window.localStorage.getItem('language123') || 'fr';

    setLang(lang, () => setMessage(langMessage[setLang]));
  },
  componentDidUpdate() {
    const ele = document.querySelector('section');
    if (ele) {
      ele.scrollTop = 0;
    }
  },
});

const NetworkError = () => (
  <GenericErrorPage
    error={{ pageTitle: 'Erreur', title: 'Erreur de connexion au serveur' }}
  />
);

const App = compose(
  withLangState,
  withMessageState,
  withLifecycle,
  withMe,
  branch(({ data }) => !!data?.loading, renderComponent(LoadingApp)),
  getChildContext,
  branch(
    ({ resetPassword }) => !!resetPassword,
    renderComponent(ResetPassword),
  ),
  withAlert(),
  msgHooksHandlers,
  withProps(({ errorMsg, infoMsg, showMsg, successMsg, removeMsg }) => ({
    msgHooks: {
      errorMsg,
      infoMsg,
      showMsg,
      successMsg,
      removeMsg,
    },
  })),
  loginHandlers,
  branch(
    ({ data }) => data?.error?.message === 'Network error: Failed to fetch',
    renderComponent(NetworkError),
  ),
  branch(
    ({ data }) => data?.error || !data?.metaQuery?.me,
    renderComponent(LoginComponent),
  ),
  withState('currentPage', 'setCurrentPage'),
  pageHandlers,
  appProps,
)(AppWithMe);

App.propTypes = {
  children: PropTypes.element.isRequired,
  context: PropTypes.shape({
    setTitle: PropTypes.func,
    setMeta: PropTypes.func,
    setLang: PropTypes.func,
    setMsgRef: PropTypes.func,
  }),
  partnerNumber: PropTypes.string,
  resetPassword: PropTypes.bool,
  withoutExtraProps: PropTypes.bool,
};

export default App;
